import { handleActions } from 'redux-actions';
import { produce } from 'immer';
import { ThemeType, USER_PERMISSION_MODEL } from '@model';

import action from '../action';
import { deepCopy } from '@utils/common';

const {
  actionTypes: { projects },
} = action;

const filterPermission = (type, permission) =>
  Object.keys(type).filter((p) => permission.some((s) => s === p));

const getDefaultSectionPermission = () => {
  let permission = window.localStorage.getItem('internalPermission');
  if (permission == null) {
    return {
      insightPermission: [],
      analysisPermission: [],
      questionnairePermission: [],
    };
  }

  permission = permission.split(',');

  return {
    analysisPermission: filterPermission(
      USER_PERMISSION_MODEL.ANALYSIS,
      permission,
    ),

    insightPermission: filterPermission(
      USER_PERMISSION_MODEL.INSIGHT,
      permission,
    ),

    questionnairePermission: filterPermission(
      USER_PERMISSION_MODEL.QUESTIONNAIRE,
      permission,
    ),
  };
};

const defaultProjectState = {
  projectDetail: {},
  userPreference: {
    shouldShowDetail: false,
  }, // 用户的配置项，目前只有shouldShowDetail
  integrationList: [],
  queryCompanyResult: null,
  teamsData: undefined,
  queryNamesResult: [],
  saveNamesResult: [],
  memberData: [],
  clientData: [],
  groupData: [],
  userPermission: [],
  internalPermission:
    (window.localStorage.getItem('internalPermission') || '').split(',') || [],
  sectionsPermission: {
    insightPermission: [],
    analysisPermission: [],
    questionnairePermission: [],
  },
  // permissionLoaded: false,
  internalSectionsPermission: getDefaultSectionPermission(),
  DUNsNumberList: {
    companyList: [],
    pagination: {},
  },
  accessibleModules: {},
  originAccessibleModules: null, // raw accessible module data
  /** Set when getAccessibleModules succeeds; used to avoid duplicate permission fetches on Project Home */
  permissionBundleProjectId: null,
  projectStatus: null, // cached project status data
  //@hook useWidget
  widgets: {},
  theme: ThemeType.light, // choose theme color between 'dark' and 'light'
};

const defaultState = {
  // isShowMainPageDropDownList: false,
  listData: [],
  queryCompanyLoading: false,
  changeQueryNamesLoading: false,
  archiveListData: [],
  pinnedProjects: [],

  createProjectDisabled: false,

  saveData: {
    currentNum: 1, // 进度条，  第一步里的小步
    lastNum: 1, // create-project里面的，判断上次的位置
    currentStep: 1, // 进度条当前的步数
    confidential: 'none',
    secondaryUse: 'none',
    isEditing: true,
    buyerMode: 'name',
    targetMode: 'name',
    buyerNotFound: false,
    targetNotFound: false,
    isSingleCompany: false,
    singleCompanyInstance: 'Integration',
    companyInstance: 'none',
    winTheEngagement: 'none',
    theme: ThemeType.light,
  },
  settingData: {
    currentNum: 1,
  },
  permissionLoading: true,
  integrationNavigatorLoading: false,
  ...deepCopy(defaultProjectState),
};

export default handleActions(
  {
    // [projects.MAIN_PAGE_DROP_DOWN_HANDLER]: (state, action) =>
    //   produce(state, (draft) => {
    //     draft.isShowMainPageDropDownList = action.payload;
    //   }),

    [projects.DUNS_NUMBER_LIST_HANDLER]: (state, action) =>
      produce(state, (draft) => {
        const DUNSlist = action.payload?.DUNsNumberList;
        draft.DUNsNumberList = DUNSlist;
      }),

    [projects.CHANGE_PROJECTS_LIST]: (state, action) =>
      produce(state, (draft) => {
        draft.listData = action.payload;
      }),

    [projects.CHANGE_ARCHIVE_LIST]: (state, action) =>
      produce(state, (draft) => {
        draft.archiveListData = action.payload;
      }),

    [projects.CHANGE_PINNED_PROJECTS]: (state, action) =>
      produce(state, (draft) => {
        draft.pinnedProjects = action.payload;
      }),

    [projects.SAVE_DATA]: (state, action) =>
      produce(state, (draft) => {
        if (action.payload.init) {
          draft.saveData = { ...defaultState.saveData };
          // temporarily revert the change
          // draft.memberData = [];
          // draft.clientData = [];
          // draft.groupData = [];
          draft.customizedModules = [];
        } else {
          draft.saveData = { ...draft.saveData, ...action.payload };
        }
        return draft;
      }),
    [projects.SETTING_PROJECT_DATA]: (state, action) =>
      produce(state, (draft) => {
        if (action.payload.clear) {
          draft.settingData = { currentNum: action.payload.currentNum };
        } else {
          if (action.payload.buyerCompanyInfo) {
            Object.keys(action.payload.buyerCompanyInfo).forEach((item) => {
              if (!action.payload.buyerCompanyInfo[item]) {
                action.payload.buyerCompanyInfo[item] = null;
              }
            });
          }
          if (action.payload.targetCompanyInfo) {
            Object.keys(action.payload.targetCompanyInfo).forEach((item) => {
              if (!action.payload.targetCompanyInfo[item]) {
                action.payload.targetCompanyInfo[item] = null;
              }
            });
          }
          draft.settingData = {
            ...draft.settingData,
            ...action.payload,
          };
        }
        return draft;
      }),

    [projects.SAVE_QUERY_COMPANY_NAME]: (state, action) =>
      produce(state, (draft) => {
        draft.queryCompanyResult = {
          ...draft.queryCompanyResult,
          ...action.payload,
        };
      }),
    [projects.CLEAR_COMPANY_NAME]: (state, action) =>
      produce(state, (draft) => {
        if (action.payload.clear) {
          draft.queryCompanyResult = {};
        } else {
          draft.queryCompanyResult = {
            ...draft.queryCompanyResult,
            ...action.payload,
          };
        }
        return draft;
      }),
    [projects.CLEAR_TEAMS_DATA]: (state, action) =>
      produce(state, (draft) => {
        draft.teamsData = undefined;
        draft.queryNamesResult = [];
        draft.saveNamesResult = [];
        draft.memberData = [];
        draft.clientData = [];
      }),
    [projects.CHANGE_QUERY_COMPANY_LOADING]: (state, action) =>
      produce(state, (draft) => {
        draft.queryCompanyLoading = action.payload;
      }),
    [projects.CREATE_PROJECT_DISABLED_HANDLER]: (state, action) =>
      produce(state, (draft) => {
        draft.createProjectDisabled = action.payload;
      }),

    [projects.SAVE_PROJECTS_STATE]: (state, action) => action.payload || state,
    [projects.SAVE_PROJECT_DETAIL]: (state, action) =>
      produce(state, (draft) => {
        draft.projectDetail = { ...draft.projectDetail, ...action.payload };
      }),
    [projects.CHANGE_USER_PREFERENCE]: (state, action) =>
      produce(state, (draft) => {
        draft.userPreference = { ...draft.userPreference, ...action.payload };
      }),
    [projects.CHANGE_TEAMS_DETAIL]: (state, action) =>
      produce(state, (draft) => {
        draft.teamsData = action.payload;
      }),
    [projects.CHANGE_TEAMS_NAMES]: (state, action) =>
      produce(state, (draft) => {
        const { data } = action.payload;
        draft.queryNamesResult = data;
        return draft;
        // draft.queryNamesResult = action.payload;
      }),
    [projects.SAVE_TEAMS_NAMES]: (state, action) =>
      produce(state, (draft) => {
        const { data, memberData } = action.payload;
        console.log('data memberData: ', data, memberData);
        draft.saveNamesResult = data;
        draft.memberData = memberData;
      }),
    [projects.CHANGE_MEMBERS_DATA]: (state, action) =>
      produce(state, (draft) => {
        const { memberData } = action.payload;
        console.log('CHANGE_MEMBERS_DATA: ', memberData);
        draft.memberData = memberData;
      }),
    [projects.CHANGE_CLIENTS_DETAIL]: (state, action) =>
      produce(state, (draft) => {
        draft.clientData = action.payload;
      }),
    [projects.CHANGE_GROUPS_DETAIL]: (state, action) =>
      produce(state, (draft) => {
        draft.groupData = action.payload;
      }),

    [projects.CHANGE_QUERY_NAMES_LOADING]: (state, action) =>
      produce(state, (draft) => {
        draft.changeQueryNamesLoading = action.payload;
      }),
    [projects.SAVE_USER_PERMISSION]: (state, action) =>
      produce(state, (draft) => {
        const { type, detailType, permission } = action.payload;
        // debugger;
        draft[type] = permission;
        if (draft[detailType]) {
          draft[detailType].analysisPermission = filterPermission(
            USER_PERMISSION_MODEL.ANALYSIS,
            permission,
          );

          draft[detailType].insightPermission = filterPermission(
            USER_PERMISSION_MODEL.INSIGHT,
            permission,
          );

          draft[detailType].questionnairePermission = filterPermission(
            USER_PERMISSION_MODEL.QUESTIONNAIRE,
            permission,
          );
        }

        window.localStorage.setItem(type, permission);
      }),

    [projects.SAVE_CUSTOMIZED_MODULES]: (state, action) =>
      produce(state, (draft) => {
        draft.customizedModules = action.payload?.customizeModule;
      }),
    [projects.SAVE_ACCESSIBLE_MODULES]: (state, action) =>
      produce(state, (draft) => {
        draft.accessibleModules = action.payload;
      }),
    [projects.SAVE_ORIGIN_ACCESSIBLE_MODULES]: (state, action) =>
      produce(state, (draft) => {
        draft.originAccessibleModules = action.payload;
      }),
    [projects.SAVE_PERMISSION_BUNDLE_PROJECT_ID]: (state, action) =>
      produce(state, (draft) => {
        draft.permissionBundleProjectId = action.payload;
      }),
    [projects.SAVE_PROJECT_STATUS]: (state, action) =>
      produce(state, (draft) => {
        draft.projectStatus = action.payload;
      }),
    [projects.CHANGE_INTEGRATION_NAVIGATOR_LOADING]: (state, action) =>
      produce(state, (draft) => {
        draft.integrationNavigatorLoading = action.payload;
      }),
    // [projects.CHANGE_PERMISSION_LOADED]: (state, action) =>
    //   produce(state, (draft) => {
    //     draft.permissionLoaded = action.payload;
    //   }),
    [projects.CHANGE_PERMISSION_LOADING]: (state, action) =>
      produce(state, (draft) => {
        draft.permissionLoading = action.payload;
      }),
    [projects.CLEAR_PROJECT_DETAIL]: (state, action) =>
      produce(state, (draft) => {
        draft.projectDetail = {};
      }),
    [projects.CHANGE_WIDGET]: (state, action) =>
      produce(state, (draft) => {
        const { widgetId, data } = action.payload;
        draft.widgets[widgetId] = data;
      }),
    [projects.DELETE_WIDGET]: (state, action) =>
      produce(state, (draft) => {
        delete draft.widgets[action.payload];
      }),
    [projects.CLEAR_WIDGETS]: (state, action) =>
      produce(state, (draft) => {
        draft.widgets = [];
      }),
    [projects.RESET_PROJECT_DATA]: (state) =>
      produce(state, (draft) => {
        Object.keys(defaultProjectState).forEach((k) => {
          draft[k] = defaultProjectState[k];
        });
      }),
    [projects.CHANGE_THEME]: (state, action) =>
      produce(state, (draft) => {
        draft.theme = action.payload;
      }),
  },
  defaultState,
);
